/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IModuleStateProps, withModuleState } from '@msdyn365-commerce-modules/checkout-utilities';
import { EmailRegex } from '@msdyn365-commerce-modules/retail-actions';
import { IModuleProps } from '@msdyn365-commerce-modules/utilities';
import { ErrorLocation } from '@msdyn365-commerce/global-state';
import classnames from 'classnames';
import { action, computed, reaction, when } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { ErrorComponent } from '../../common';

import { ICheckoutGuestProfileData } from './checkout-guest-profile.data';
import { ICheckoutGuestProfileProps } from './checkout-guest-profile.props.autogenerated';
import getAddContactInfo, { IAddContactInfo } from './components/get-add-contact-info';
import getShowContactInfo, { IShowContactInfo } from './components/get-show-contact-info';

export * from './components/get-add-contact-info';
export * from './components/get-show-contact-info';

interface ICheckoutGuestProfileState {
    email: string;
    hasCheckoutError?: boolean;
    errorMessage?: string;
}

export interface ICheckoutGuestProfileModuleProps extends ICheckoutGuestProfileProps<ICheckoutGuestProfileData>, IModuleStateProps {}

export interface ICheckoutGuestProfileViewProps extends ICheckoutGuestProfileProps<ICheckoutGuestProfileData>, ICheckoutGuestProfileState {
    moduleProps: IModuleProps;
    showContactInfo?: IShowContactInfo;
    addContactInfo?: IAddContactInfo;
    alert?: React.ReactNode;

    onChange?(email: string): void;
    onEdit?(): void;
    onSubmit?(): void;
    onCancel?(): void;
}

/**
 *
 * CheckoutGuestProfile component.
 * @extends {React.PureComponent<ICheckoutGuestProfileProps<ICheckoutGuestProfileData>, ICheckoutGuestProfileState>}
 */
// @ts-expect-error
@withModuleState
@observer
class CheckoutGuestProfile extends React.Component<ICheckoutGuestProfileModuleProps, ICheckoutGuestProfileState> {
    public state: ICheckoutGuestProfileState = {
        email:
            (this.props.context &&
                this.props.context.request &&
                this.props.context.request.user &&
                this.props.context.request.user.isAuthenticated &&
                this.props.context.request.user.emailAddress) ||
            ''
    };

    private readonly inputRef: React.RefObject<HTMLInputElement> = React.createRef();

    @computed get isDataReady(): boolean {
        return (this.props.data.checkout.result && this.props.data.checkout.status) === 'SUCCESS';
    }

    @computed get hasEmailError(): boolean {
        return this.props.moduleState.hasError && !this.state.hasCheckoutError;
    }

    public componentDidMount(): void {
        when(
            () => this.isDataReady,
            async () => {
                await this.init();
            }
        );

        reaction(
            () => this.props.data.checkout.result?.isExpressCheckoutApplied,
            async isExpressCheckoutApplied => {
                if (isExpressCheckoutApplied && !this.props.data.checkout.result?.isExpressAddressApplied) {
                    const email = this.props.data.checkout.result?.emailAddressFromExpressCheckout ?? '';

                    await this.submitEmail(email);
                }
            }
        );

        if (this.props.data.checkout.result?.shouldEnableCheckoutErrorDisplayMessaging) {
            reaction(
                () => this.props.data.checkout.result?.checkoutError,
                checkoutError => {
                    if (checkoutError && checkoutError.errorLocation === ErrorLocation.CheckoutGuestProfile && checkoutError.errorMessage) {
                        this._setErrorMessage(checkoutError.errorMessage);
                    }
                }
            );
        }
    }

    public shouldComponentUpdate(nextProps: ICheckoutGuestProfileModuleProps, nextState: ICheckoutGuestProfileState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element {
        const {
            config: { className },
            resources,
            moduleState: { isReady, hasExternalSubmitGroup }
        } = this.props;
        const { hasCheckoutError, errorMessage } = this.state;
        const email = this.props.data.checkout.result?.guestCheckoutEmail ?? '';

        const viewProps: ICheckoutGuestProfileViewProps = {
            ...this.props,
            ...this.state,
            onChange: this.onChange,
            onEdit: this.onEdit,
            onCancel: this.onCancel,
            onSubmit: this.onSubmit,

            moduleProps: {
                moduleProps: this.props,
                className: classnames('ms-checkout-guest-profile', className, isReady ? 'show' : 'add')
            },
            alert: hasCheckoutError && errorMessage && (
                <ErrorComponent {...{ title: resources.errorMessageTitle, message: errorMessage }} />
            ),
            showContactInfo: isReady
                ? getShowContactInfo({
                      canEdit: !hasExternalSubmitGroup,
                      email,
                      resources,
                      onEdit: this.onEdit
                  })
                : undefined,
            addContactInfo: !isReady
                ? getAddContactInfo({
                      email: this.state.email,
                      hasError: this.hasEmailError,
                      resources,
                      inputRef: this.inputRef,
                      onChange: this.onChange,
                      canSubmit: !hasExternalSubmitGroup,
                      onSubmit: this.onSubmit,
                      canCancel: !hasExternalSubmitGroup && !!email,
                      onCancel: this.onCancel
                  })
                : undefined
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private readonly init = async (): Promise<void> => {
        const checkoutState = this.props.data.checkout.result;

        const hasInvoiceLine = checkoutState?.checkoutCart.hasInvoiceLine;

        const emailOnCart = checkoutState?.checkoutCart.cart.ReceiptEmail;
        const email = hasInvoiceLine
            ? this.props.context.request.user.emailAddress ?? ''
            : emailOnCart ??
              (checkoutState?.isExpressCheckoutApplied ? checkoutState.emailAddressFromExpressCheckout : undefined) ??
              this.state.email;

        if (email) {
            await this.updateGuestProfile(email);
            checkoutState?.updateHasGuestCheckoutEmail({ newHasGuestCheckoutEmail: true });
        } else {
            checkoutState?.updateHasGuestCheckoutEmail({ newHasGuestCheckoutEmail: false });
        }

        const isEmailValid = this.isEmailValid(email);
        this.props.moduleState.init({
            onEdit: this.onEdit,
            onCancel: this.onCancel,
            onSubmit: this.onSubmit,
            status: hasInvoiceLine ? 'disabled' : email && isEmailValid ? 'ready' : 'updating',
            hasError: !!email && !isEmailValid
        });
    };

    private readonly onChange = (rowEmail: string): void => {
        const email = (rowEmail || '').replace(new RegExp('[<>]', 'gi'), '');
        this.setState({ email });
        if (this.props.moduleState.hasError) {
            // Clear error during user updating the email
            this.props.moduleState.setHasError(false);
            this.setState({
                hasCheckoutError: false
            });
        }
    };

    private readonly isEmailValid = (email: string): boolean => {
        return EmailRegex.defaultRegex.test(email);
    };

    private readonly updateGuestProfile = async (newGuestCheckoutEmail: string): Promise<void> => {
        if (this.props.data.checkout.result) {
            await this.props.data.checkout.result.updateGuestCheckoutEmail({ newGuestCheckoutEmail });
            this.setState({ email: newGuestCheckoutEmail });
        }
    };

    private readonly onEdit = (): void => {
        this.props.telemetry.information('Guest profile onEdit is called.');

        this.props.moduleState.onUpdating();
    };

    private readonly onSubmit = async (): Promise<void> => {
        this.props.telemetry.information('Guest profile onSubmit is called.');

        const { email } = this.state;
        await this.submitEmail(email);
    };

    /**
     * Submit the email address.
     * @param email - The email to be submitted.
     *
     */
    private readonly submitEmail = async (email: string): Promise<void> => {
        const isValid = this.isEmailValid(email);
        if (!isValid) {
            this.props.moduleState.setHasError(true);

            const input = this.inputRef.current?.focus && (this.inputRef.current as HTMLElement);
            input?.focus();
        } else {
            this.props.moduleState.onReady();
            await this.updateGuestProfile(email);
        }
    };

    private readonly onCancel = (): void => {
        this.props.telemetry.information('Guest profile onCancel is called.');

        // Reset to the saved value
        if (!this.props.data.checkout.result) {
            return;
        }

        const email = this.props.data.checkout.result.guestCheckoutEmail;
        if (!email) {
            // It has no saved email, set to status updating and request user to fill the email form
            this.props.moduleState.onUpdating();
        } else {
            // It has saved email, set to status ready
            this.setState({
                email
            });
            this.props.moduleState.setHasError(false);
            this.setState({
                hasCheckoutError: false
            });
            this.props.moduleState.onReady();
        }
    };

    @action
    private readonly _setErrorMessage = (errorMessage: string): void => {
        this.props.telemetry.error(errorMessage);
        this.props.moduleState.setHasError(true);
        this.props.moduleState.onUpdating();
        this.setState({
            hasCheckoutError: true,
            errorMessage
        });
    };
}

export default CheckoutGuestProfile;
